/**
 * Rôle métier d'une colonne du fichier importé.
 *
 * Les valeurs correspondent à l'énumération `ImportColumnRole` côté API : elles
 * transitent en clair dans le mapping, il ne faut donc pas les renuméroter.
 */
export enum ImportColumnRole {
  Ignore = 0,
  Question = 1,
  Theme = 2,
  Level = 3,
  Skill = 4,
  Prerequisites = 5,
  Targeted = 6,
  Tags = 7,
}

/** Fiabilité attendue de l'extraction, selon le format du fichier. */
export type ImportConfidence = 'High' | 'Approximate';

/** Contenu brut d'une cellule, tel qu'extrait. */
export interface ImportedCell {
  index: number;
  text: string;
  /** Lignes conservées séparément : c'est sur elles que porte le surlignage. */
  lines: string[];
  /** Indices, dans `lines`, des lignes surlignées. */
  highlightedLines: number[];
  /** Indices des lignes mises en gras — repli quand rien n'est surligné. */
  emphasizedLines: number[];
}

/** Une ligne du tableau, sans interprétation. */
export interface ImportedRow {
  rowNumber: number;
  pageNumber: number;
  cells: ImportedCell[];
}

/** Résultat de l'analyse d'un fichier. */
export interface TestImportPreview {
  objectKey: string;
  format: string;
  language: string;
  direction: string;
  confidence: ImportConfidence;
  pagesProcessed: number;
  columnCount: number;
  /** En-têtes détectés, vides si le tableau n'en porte pas. */
  headerLabels: string[];
  rows: ImportedRow[];
  /** Proposition de mapping, indexée par position de colonne. */
  suggestedMapping: Record<number, ImportColumnRole>;
  warnings: string[];
}

export interface ImportedAnswer {
  content: string;
  isCorrect: boolean;
}

/** Question telle qu'elle serait créée, une fois le mapping appliqué. */
export interface ImportedQuestion {
  rowNumber: number;
  pageNumber: number;
  title: string;
  answers: ImportedAnswer[];
  theme?: string | null;
  level?: string | null;
  skill?: string | null;
  prerequisites?: string | null;
  targeted?: string | null;
  tags: string[];
  contentHash?: string | null;
  /** Renseigné si une question identique existe déjà en banque. */
  duplicateOfQuestionId?: number | null;

  /**
   * Numéro de la ligne, plus haut dans le **même fichier**, qui porte déjà cet
   * énoncé.
   *
   * <p>À ne pas confondre avec {@link duplicateOfQuestionId}, qui désigne la
   * banque : la question déjà en banque est reprise dans le test, la simple
   * répétition du document n'y entre qu'une fois. Les compter pareillement
   * annonçait plus de questions que le test n'en poserait.</p>
   */
  repeatOfRowNumber?: number | null;
  warnings: string[];
}

/** Paramètres du dépôt. */
export interface TestImportRequest {
  objectKey: string;
  subjectId: number;
  language: string;
  direction: string;
}

/** Ce que l'on renvoie une fois le mapping choisi et les lignes relues. */
export interface TestImportCommit extends TestImportRequest {
  mapping: Record<number, ImportColumnRole>;

  /**
   * Format du document analysé (`docx`, `pdf`), tel que l'analyse l'a
   * renvoyé. Dans un Word, chaque ligne de l'énoncé est un retour voulu et se
   * conserve ; dans un PDF, elle est coupée à la largeur de la page.
   */
  format?: string;

  /**
   * Parcours supplémentaires recevant le même référentiel, chacun avec la
   * matière qui l'y reçoit et le professeur qui y sera titulaire du test.
   *
   * <p>Vide, l'import ne vise que la matière de dépôt.</p>
   */
  additionalTargets?: ImportTargetPayload[];
  rows: ImportedRow[];

  /**
   * Questions telles que l'écran de contrôle les a laissées.
   *
   * <p><b>Pourquoi ce champ existe.</b> L'écran permettait déjà de désigner la
   * bonne réponse, mais le commit ne portait que {@link rows} — les cellules
   * brutes — et le serveur reprojetait tout. La correction était donc
   * <b>silencieusement perdue</b> : le professeur cochait, et l'import posait
   * la réponse que le parseur avait cru lire.</p>
   *
   * <p><b>Le partage.</b> Le client fournit le contenu ; le serveur garde les
   * décisions — il recalcule l'empreinte, refait la détection des doublons,
   * résout compétences et niveaux, et valide ce qu'il reçoit.</p>
   *
   * <p>Envoyé <b>au commit seulement</b>. L'aperçu doit montrer <i>le
   * fichier</i> : s'il renvoyait l'écho de ce qu'on lui passe, une erreur de
   * lecture deviendrait invisible.</p>
   */
  questions?: ImportedQuestion[];
  /** Titre du test à composer. Vide, les questions rejoignent la seule banque. */
  testTitle?: string | null;

  /**
   * Importer bien que la matière porte déjà tout ce référentiel.
   *
   * <p><b>Pourquoi ce champ existe.</b> L'API refusait déjà de réimporter un
   * test dans une matière qui l'a — mais la garde ne regardait que le
   * <i>titre du test</i>. Un second dépôt <b>sans titre</b> partait en banque
   * seule sans aucun contrôle, et doublait silencieusement le référentiel.</p>
   *
   * <p>La garde porte désormais sur le contenu. Ce champ la lève : le refus
   * reste le défaut, passer outre est un geste explicite — l'écran ne propose
   * la case que lorsque la garde va effectivement se déclencher.</p>
   */
  allowDuplicateImport?: boolean;

  /**
   * Réglages du test composé, mêmes champs que le formulaire de création.
   * Ignorés sans titre : il n'y a alors pas de test à paramétrer.
   */
  description?: string | null;
  timeLimitMinutes?: number | null;
  /**
   * Barème du test composé. Omis, le serveur retient 20.
   *
   * <p>Les questions importées valent toutes 1 point : rien n'est réparti au
   * moment de l'import, c'est la restitution qui ramène le résultat sur ce
   * barème. Une question réutilisée dans un autre test y garde donc sa
   * valeur.</p>
   */
  gradingScale?: number | null;
  passingPercentage?: number | null;
  maxAttempts?: number | null;
  showResultsImmediately?: boolean | null;
  allowReview?: boolean | null;

  /**
   * Professeur responsable des questions créées, et titulaire du test composé.
   * Renseigné par l'administrateur seul : un professeur importe pour lui-même
   * et l'API ignore alors ce champ.
   */
  professorId?: number | null;
}

/** Bilan d'un import écrit en base. */
export interface TestImportResult {
  batchId: number;
  questionsCreated: number;
  questionsSkipped: number;
  themesCreated: number;
  domainsCreated: number;
  competenciesCreated: number;
  gradeLevelsCreated: number;
  prerequisitesLinked: number;
  testId?: number | null;

  /** Ce qui a été écrit dans chaque matière visée. Les compteurs ci-dessus en sont la somme. */
  targets?: TestImportTargetResult[];

  warnings: string[];
}

/** Ce qu'un import a produit dans une matière. */
export interface TestImportTargetResult {
  subjectId: number;
  subjectName?: string | null;
  learningPathId?: number | null;
  learningPathTitle?: string | null;
  questionsCreated: number;
  questionsSkipped: number;

  /**
   * Vrai quand un test du même intitulé existait déjà dans cette matière : rien
   * n'y a été importé, ni question ni test.
   *
   * <p>C'est la seule chose que l'import refuse. Les questions, elles, sont
   * toujours créées — le document fait foi.</p>
   */
  alreadyImported?: boolean;
  themesCreated: number;
  testId?: number | null;

  /**
   * Vrai quand un titre de test était demandé mais qu'aucun test n'a pu être
   * composé ici, faute de titulaire. Les questions sont bien créées — elles
   * rejoignent la seule banque.
   */
  bankOnly?: boolean;

  warnings: string[];
}

/** Une matière visée, et le professeur qui y sera titulaire du test composé. */
export interface ImportTargetPayload {
  subjectId: number;
  professorId?: number | null;
}

/**
 * Ce qu'une matière visée recevrait.
 *
 * <p>Les questions sont les mêmes d'une cible à l'autre — c'est le même
 * fichier. Leur statut de doublon, non : une question peut exister dans un
 * parcours et pas dans l'autre.</p>
 */
export interface TestImportTargetPreview {
  subjectId: number;
  subjectName?: string | null;
  learningPathId?: number | null;
  learningPathTitle?: string | null;
  questions: ImportedQuestion[];
}
